import { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Divider,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  Replay as RetryIcon,
  Delete as DismissIcon,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { db } from '@db/database';
import type { SyncQueueItem, SyncEntityType } from '@db/database';
import { useUserStore } from '@stores/userStore';
import {
  syncNow,
  retryFailed,
  dismissItem,
  subscribe
} from '@services/offlineSyncService';
import useSyncStatus from '@hooks/useSyncStatus';

const ENTITY_TYPE_LABELS: Record<SyncEntityType, string> = {
  calibration_save: 'Save Calibration',
  acknowledgment: 'Customer Acknowledgment',
  calibration_complete: 'Complete Calibration',
  dc_runtime_create: 'Create Calibration Record',
  wo_detail_complete: 'Complete WO Detail',
  wo_detail_skip: 'Skip WO Detail',
  wo_detail_incomplete: 'Reopen WO Detail',
  wo_complete: 'Complete Work Order',
  wo_incomplete: 'Reopen Work Order',
  charge_add: 'Add Charge',
  charge_delete: 'Delete Charge',
  wo_document_create: 'Create Document'
};

const StatusIcon = ({ status }: { status: SyncQueueItem['status'] }) => {
  switch (status) {
    case 'synced':
      return <CheckCircle color="success" fontSize="small" />;
    case 'syncing':
      return <CircularProgress size={18} />;
    case 'pending':
      return <Schedule color="action" fontSize="small" />;
    case 'failed':
      return <ErrorIcon color="error" fontSize="small" />;
  }
};

type GroupedItems = Record<string, SyncQueueItem[]>;

interface SyncQueueDrawerProps {
  open: boolean;
  onClose: () => void;
}

const SyncQueueDrawer = ({ open, onClose }: SyncQueueDrawerProps) => {
  const { failed } = useSyncStatus();
  const [items, setItems] = useState<SyncQueueItem[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const loadItems = useCallback(async () => {
    const userId = useUserStore.getState().userName;
    if (!userId) return;

    const queueItems = await db.syncQueue
      .where('_userId')
      .equals(userId)
      .and(i => i.status !== 'synced')
      .toArray();

    queueItems.sort((a, b) => {
      const statusOrder = { failed: 0, syncing: 1, pending: 2, synced: 3 };
      const diff = statusOrder[a.status] - statusOrder[b.status];
      if (diff !== 0) return diff;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    setItems(queueItems);
  }, []);

  useEffect(() => {
    if (open) {
      loadItems();
      return subscribe(loadItems);
    }
  }, [open, loadItems]);

  const grouped: GroupedItems = items.reduce<GroupedItems>((acc, item) => {
    const jobNumber =
      ((item.payload as Record<string, unknown>).jobNumber as string) ||
      item.entityKey.split(':')[0] ||
      'Other';
    if (!acc[jobNumber]) acc[jobNumber] = [];
    acc[jobNumber].push(item);
    return acc;
  }, {});

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSyncNow = () => {
    syncNow();
  };

  const handleRetryAll = () => {
    retryFailed();
  };

  const groupKeys = Object.keys(grouped);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 380 },
          boxSizing: 'border-box'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Sync Queue
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {items.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4
              }}
            >
              <Typography color="text.secondary" variant="body2">
                No pending sync items
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {groupKeys.map(jobNumber => {
                const groupItems = grouped[jobNumber];
                const isExpanded =
                  expandedGroups.has(jobNumber) || groupKeys.length <= 3;
                const failedCount = groupItems.filter(
                  i => i.status === 'failed'
                ).length;

                return (
                  <Box key={jobNumber}>
                    <ListItem
                      component="div"
                      onClick={() => toggleGroup(jobNumber)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              {jobNumber}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({groupItems.length} item
                              {groupItems.length !== 1 ? 's' : ''})
                            </Typography>
                            {failedCount > 0 && (
                              <Typography
                                variant="caption"
                                color="error.main"
                                sx={{ fontWeight: 600 }}
                              >
                                {failedCount} failed
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {groupKeys.length > 3 &&
                        (isExpanded ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        ))}
                    </ListItem>

                    <Collapse in={isExpanded}>
                      <List disablePadding>
                        {groupItems.map(item => {
                          const isPermanentlyFailed =
                            item.status === 'failed' &&
                            item.retryCount >= item.maxRetries;

                          return (
                            <ListItem
                              key={item.id}
                              sx={{ pl: 3, py: 0.75 }}
                              secondaryAction={
                                item.status === 'failed' ? (
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => retryFailed()}
                                      title="Retry"
                                    >
                                      <RetryIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => dismissItem(item.id!)}
                                      title="Dismiss"
                                    >
                                      <DismissIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : undefined
                              }
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <StatusIcon status={item.status} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {ENTITY_TYPE_LABELS[item.entityType] ||
                                      item.entityType}
                                  </Typography>
                                }
                                secondary={
                                  item.status === 'failed' ? (
                                    <Box component="span">
                                      {isPermanentlyFailed && (
                                        <Typography
                                          variant="caption"
                                          color="error.main"
                                          sx={{
                                            display: 'block',
                                            fontWeight: 600,
                                            mt: 0.25
                                          }}
                                        >
                                          Permanently failed ({item.retryCount}/
                                          {item.maxRetries} retries)
                                        </Typography>
                                      )}
                                      {item.lastError && (
                                        <Typography
                                          variant="caption"
                                          color="error.main"
                                          sx={{
                                            display: 'block',
                                            mt: 0.25,
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          {item.lastError}
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : undefined
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                    <Divider />
                  </Box>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer actions */}
        {items.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<SyncIcon />}
              onClick={handleSyncNow}
              fullWidth
            >
              Sync Now
            </Button>
            {failed > 0 && (
              <Button
                variant="contained"
                size="small"
                color="error"
                startIcon={<RetryIcon />}
                onClick={handleRetryAll}
                fullWidth
              >
                Retry All Failed
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default SyncQueueDrawer;
